import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  MenuItem,
  Select,
  Typography,
  Box,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import {
  RepairStatusType,
  statusColors,
} from "../../../data/FormColumns/Fleet/repairsTableColumns";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  TooltipItem,
} from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineController,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
);

enum TimeRange {
  Week = "Week",
  Month = "Month",
  Year = "Year",
}

interface RepairCategorySpendDto {
  repairCategory: string;
  totalCost: number;
  workOrdersCount: number;
  averageCostPerWorkOrder: number;
  percentOfTotalSpend: number;
}

interface RepairCategorySpendSummaryDto {
  grandTotal: number;
  workOrdersCount: number;
  categories: RepairCategorySpendDto[];
}

const formatRepairCategoryName = (category: string) => {
  const categoryWithSpaces = category
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim()
    .toLowerCase();

  return categoryWithSpaces
    ? categoryWithSpaces.charAt(0).toUpperCase() + categoryWithSpaces.slice(1)
    : category;
};

const fetchTotalIncidents = async (
  range: string,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/totalIncidentsForCurrent${range}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchTotalYardRepairs = async (
  range: string,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/totalYardRepairsForCurrent${range}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchIncidentsByCategory = async (
  range: string,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/incidentsByTypeForCurrent${range}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchIncidentsByStatus = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/incidentsByStatusInTotal`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};
const fetchRepairSpendByCategory = async (
  token: string | null | undefined,
  month: number,
  year: number,
) => {
  const { data } = await axios.get<RepairCategorySpendSummaryDto>(
    `${apiVariable}/mostUsedRepairShops`,
    {
      params: { month, year },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};
const fetchIncidentsByMonth = async (
  token: string | null | undefined,
  year: number,
) => {
  const { data } = await axios.get(
    `${apiVariable}/totalIncidentsByMonthForCurrentYear`,
    {
      params: { year },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};
const fetchYardRepairsByMonth = async (
  token: string | null | undefined,
  year: number,
) => {
  const { data } = await axios.get(
    `${apiVariable}/totalYardRepairsByMonthForCurrentYear`,
    {
      params: { year },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchTotalTrucksCountByType = async (
  token: string | null | undefined,
) => {
  const { data } = await axios.get(`${apiVariable}/totalTrucksCountByType`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

const fetchTotalTrailersCountByType = async (
  token: string | null | undefined,
) => {
  const { data } = await axios.get(`${apiVariable}/totalTrailersCountByType`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

const StatisticsCard = () => {
  const { token } = useAuth();
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const [incidentRange, setIncidentRange] = useState<TimeRange>(TimeRange.Week);
  const [yardRepairsRange, setYardRepairsRange] = useState<TimeRange>(
    TimeRange.Week,
  );
  const [incidentsGraphYear, setIncidentsGraphYear] =
    useState<number>(currentYear);
  const [yardRepairsGraphYear, setYardRepairsGraphYear] =
    useState<number>(currentYear);
  const [repairSpendMonth, setRepairSpendMonth] =
    useState<number>(currentMonth);
  const [repairSpendYear, setRepairSpendYear] = useState<number>(currentYear);

  const changeRepairSpendMonth = (monthOffset: number) => {
    const selectedDate = new Date(
      repairSpendYear,
      repairSpendMonth - 1 + monthOffset,
    );

    setRepairSpendMonth(selectedDate.getMonth() + 1);
    setRepairSpendYear(selectedDate.getFullYear());
  };

  const { data: incidentData, isLoading: isLoadingIncidents } = useQuery({
    queryKey: ["incidentData", incidentRange],
    queryFn: () => fetchTotalIncidents(incidentRange, token),
  });

  const { data: yardRepairsData, isLoading: isLoadingYardRepairs } = useQuery({
    queryKey: ["yardRepairsData", yardRepairsRange],
    queryFn: () => fetchTotalYardRepairs(yardRepairsRange, token),
  });

  const { data: incidentsByCategory, isLoading: isLoadingIncidentsByCategory } =
    useQuery({
      queryKey: ["incidentsByCategory", incidentRange],
      queryFn: () => fetchIncidentsByCategory(incidentRange, token),
    });

  const { data: incidentsByStatus, isLoading: isLoadingIncidentsByStatus } =
    useQuery({
      queryKey: ["incidentsByStatus"],
      queryFn: () => fetchIncidentsByStatus(token),
    });
  const {
    data: repairSpendByCategory,
    isLoading: isLoadingRepairSpendByCategory,
  } = useQuery<RepairCategorySpendSummaryDto>({
    queryKey: ["repairSpendByCategory", repairSpendMonth, repairSpendYear],
    queryFn: () =>
      fetchRepairSpendByCategory(token, repairSpendMonth, repairSpendYear),
  });

  const { data: incidentsByMonth, isLoading: isLoadingIncidentsByMonth } =
    useQuery({
      queryKey: ["incidentsByMonth", incidentsGraphYear],
      queryFn: () => fetchIncidentsByMonth(token, incidentsGraphYear),
    });

  const { data: yardRepairsByMonth, isLoading: isLoadingYardRepairsByMonth } =
    useQuery({
      queryKey: ["yardRepairsByMonth", yardRepairsGraphYear],
      queryFn: () => fetchYardRepairsByMonth(token, yardRepairsGraphYear),
    });

  const {
    data: totalTrucksCountByType,
    isLoading: isLoadingTotalTrucksCountByType,
  } = useQuery({
    queryKey: ["totalTrucksCountByType"],
    queryFn: () => fetchTotalTrucksCountByType(token),
  });

  const {
    data: totalTrailersCountByType,
    isLoading: isLoadingTotalTrailersCountByType,
  } = useQuery({
    queryKey: ["totalTrailersCountByType"],
    queryFn: () => fetchTotalTrailersCountByType(token),
  });

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const percentFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Incidents (Count vs Amount)
  const incidentsChartData: ChartData<"bar" | "line"> | null = incidentsByMonth
    ? {
        labels: incidentsByMonth.map((m: { month: number }) =>
          new Date(2000, m.month - 1).toLocaleString("en-US", {
            month: "short",
          }),
        ),
        datasets: [
          {
            type: "bar" as const,
            label: "Amount ($)",
            data: incidentsByMonth.map(
              (m: { totalAmount: any }) => m.totalAmount,
            ),
            backgroundColor: "rgba(75,192,192,0.6)",
            borderColor: "rgba(75,192,192,1)",
            borderWidth: 1,
            barPercentage: 0.4,
            yAxisID: "yAmount",
          },
          {
            type: "line" as const,
            label: "Incidents",
            data: incidentsByMonth.map(
              (m: { totalIncidents: any }) => m.totalIncidents,
            ),
            borderColor: "rgba(153,102,255,1)",
            backgroundColor: "rgba(153,102,255,0.2)",
            borderWidth: 2,
            tension: 0.3,
            yAxisID: "yCount",
          },
        ],
      }
    : null;

  // Yard Repairs (Count vs Amount)
  const yardRepairsChartData: ChartData<"bar" | "line"> | null =
    yardRepairsByMonth
      ? {
          labels: yardRepairsByMonth.map((m: { month: number }) =>
            new Date(2000, m.month - 1).toLocaleString("en-US", {
              month: "short",
            }),
          ),
          datasets: [
            {
              type: "bar" as const,
              label: "Amount ($)",
              data: yardRepairsByMonth.map(
                (m: { totalAmount: any }) => m.totalAmount,
              ),
              backgroundColor: "rgba(255,159,64,0.6)",
              borderColor: "rgba(255,159,64,1)",
              borderWidth: 1,
              barPercentage: 0.4,
              yAxisID: "yAmount",
            },
            {
              type: "line" as const,
              label: "Repairs",
              data: yardRepairsByMonth.map(
                (m: { totalIncidents: any }) => m.totalIncidents,
              ),
              borderColor: "rgba(54,162,235,1)",
              backgroundColor: "rgba(54,162,235,0.2)",
              borderWidth: 2,
              tension: 0.3,
              yAxisID: "yCount",
            },
          ],
        }
      : null;

  // 3. Shared chart options with two y-axes
  const multiAxisOptions: ChartOptions<"bar" | "line"> = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          label(ctx: TooltipItem<"line" | "bar">) {
            const label = ctx.dataset.label || "Unknown";
            if (label === "Amount ($)") {
              return `Amount: $${(ctx.raw as number).toLocaleString()}`;
            } else {
              return `${label}: ${ctx.raw}`;
            }
          },
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Month" } },
      yCount: {
        type: "linear",
        position: "left",
        title: { display: true, text: "Count" },
      },
      yAmount: {
        type: "linear",
        position: "right",
        grid: { drawOnChartArea: false },
        title: { display: true, text: "Amount ($)" },
      },
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Grid container spacing={4} maxWidth="lg">
        {/* Row 1 */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="OTR Repairs"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={incidentRange}
                    onChange={(e) =>
                      setIncidentRange(e.target.value as TimeRange)
                    }
                  >
                    <MenuItem value={TimeRange.Week}>1 Week</MenuItem>
                    <MenuItem value={TimeRange.Month}>1 Month</MenuItem>
                    <MenuItem value={TimeRange.Year}>1 Year</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent>
              {isLoadingIncidents ? (
                <Typography>Loading...</Typography>
              ) : (
                <Typography variant="h4" color="primary">
                  {incidentData}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Yard Repairs"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={yardRepairsRange}
                    onChange={(e) =>
                      setYardRepairsRange(e.target.value as TimeRange)
                    }
                  >
                    <MenuItem value={TimeRange.Week}>1 Week</MenuItem>
                    <MenuItem value={TimeRange.Month}>1 Month</MenuItem>
                    <MenuItem value={TimeRange.Year}>1 Year</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent>
              {isLoadingYardRepairs ? (
                <Typography>Loading...</Typography>
              ) : (
                <Typography variant="h4" color="secondary">
                  {yardRepairsData}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 2 */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="OTR Repairs by Category"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {isLoadingIncidentsByCategory ? (
                <Typography>Loading...</Typography>
              ) : incidentsByCategory && incidentsByCategory.length > 0 ? (
                incidentsByCategory.map((item: any) => (
                  <Typography key={item.incidentType}>
                    {`${camelCaseToSentenceCase(item.incidentType)}: ${
                      item.totalIncidents
                    }`}
                  </Typography>
                ))
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="OTR Repairs by Status"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {isLoadingIncidentsByStatus ? (
                <Typography>Loading...</Typography>
              ) : incidentsByStatus && incidentsByStatus.length > 0 ? (
                incidentsByStatus.map((item: any) => (
                  <Chip
                    key={item.status}
                    label={
                      statusColors[item.status as RepairStatusType].label +
                      `: ${item.total}`
                    }
                    color={statusColors[item.status as RepairStatusType].color}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "150px",
                      marginLeft: "auto",
                      marginRight: "auto",
                      marginTop: "5px",
                    }}
                  />
                ))
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 3: Trucks & Trailers Counts */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Trucks Count by Type"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {isLoadingTotalTrucksCountByType ? (
                <Typography>Loading...</Typography>
              ) : totalTrucksCountByType.length > 0 ? (
                totalTrucksCountByType.map((item: any) => (
                  <Typography key={item.type}>
                    {`${item.transactionType}: ${item.totalAssets}`}
                  </Typography>
                ))
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Trailers Count by Type"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <CardContent>
              {isLoadingTotalTrailersCountByType ? (
                <Typography>Loading...</Typography>
              ) : totalTrailersCountByType.length > 0 ? (
                totalTrailersCountByType.map((item: any) => (
                  <Typography key={item.type}>
                    {`${item.transactionType}: ${item.totalAssets}`}
                  </Typography>
                ))
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 4: Repair Shops & Authority Transactions */}
        <Grid container item xs={12} spacing={4}>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                backgroundColor: "#ffffff",
                textAlign: "center",
                flex: 1,
              }}
            >
              <CardHeader
                className="responsive-card-header"
                title="Spend by Repair Category"
                titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
                action={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => changeRepairSpendMonth(-1)}
                      aria-label="Previous repair spend month"
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ minWidth: 110, textAlign: "center" }}
                    >
                      {new Date(
                        repairSpendYear,
                        repairSpendMonth - 1,
                      ).toLocaleString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => changeRepairSpendMonth(1)}
                      disabled={
                        repairSpendYear > currentYear ||
                        (repairSpendYear === currentYear &&
                          repairSpendMonth >= currentMonth)
                      }
                      aria-label="Next repair spend month"
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                }
              />
              <CardContent>
                {isLoadingRepairSpendByCategory ? (
                  <Typography>Loading...</Typography>
                ) : repairSpendByCategory?.categories?.length ? (
                  <Box sx={{ width: "100%" }}>
                    <List disablePadding>
                      {repairSpendByCategory.categories.map((category) => (
                        <ListItem
                          key={category.repairCategory}
                          disablePadding
                          sx={{
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            py: 1,
                          }}
                        >
                          <ListItemText
                            primary={formatRepairCategoryName(
                              category.repairCategory,
                            )}
                            secondary={`Total: ${currencyFormatter.format(
                              category.totalCost,
                            )} • Work Orders: ${category.workOrdersCount} • Avg: ${currencyFormatter.format(
                              category.averageCostPerWorkOrder,
                            )} • ${percentFormatter.format(
                              category.percentOfTotalSpend,
                            )}% of spend`}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Grand Total:{" "}
                      {currencyFormatter.format(
                        repairSpendByCategory.grandTotal,
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Work Orders: {repairSpendByCategory.workOrdersCount}
                    </Typography>
                  </Box>
                ) : (
                  <Typography>No data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* Row 5: Incidents (Count vs Amount) */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: 3, borderRadius: 2, textAlign: "center" }}>
            <CardHeader
              className="responsive-card-header"
              title="Incidents (Count vs Amount)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setIncidentsGraphYear((previous) => previous - 1)
                    }
                    aria-label="Previous incidents year"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="body2" fontWeight={600}>
                    {incidentsGraphYear}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setIncidentsGraphYear((previous) =>
                        Math.min(previous + 1, currentYear),
                      )
                    }
                    disabled={incidentsGraphYear >= currentYear}
                    aria-label="Next incidents year"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              }
            />
            <CardContent>
              {isLoadingIncidentsByMonth ? (
                <Typography>Loading...</Typography>
              ) : incidentsChartData ? (
                <Chart
                  type="bar"
                  data={incidentsChartData}
                  options={multiAxisOptions}
                  height={100}
                  key={JSON.stringify(incidentsChartData)}
                />
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        {/* Row 6: Yard Repairs (Count vs Amount) */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: 3, borderRadius: 2, textAlign: "center" }}>
            <CardHeader
              className="responsive-card-header"
              title="Yard Repairs (Count vs Amount)"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setYardRepairsGraphYear((previous) => previous - 1)
                    }
                    aria-label="Previous yard repairs year"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="body2" fontWeight={600}>
                    {yardRepairsGraphYear}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setYardRepairsGraphYear((previous) =>
                        Math.min(previous + 1, currentYear),
                      )
                    }
                    disabled={yardRepairsGraphYear >= currentYear}
                    aria-label="Next yard repairs year"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              }
            />
            <CardContent>
              {isLoadingYardRepairsByMonth ? (
                <Typography>Loading...</Typography>
              ) : yardRepairsChartData ? (
                <Chart
                  type="bar"
                  data={yardRepairsChartData}
                  options={multiAxisOptions}
                  height={100}
                  key={JSON.stringify(yardRepairsChartData)}
                />
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StatisticsCard;
